import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "resident-notification-drawer__close-trigger" }
const _hoisted_2 = {
  key: 0,
  class: "resident-notification-drawer__loading"
}
const _hoisted_3 = { class: "resident-notification-drawer__meta" }
const _hoisted_4 = { class: "field-with-label" }
const _hoisted_5 = { class: "field-with-label__value" }
const _hoisted_6 = { class: "field-with-label" }
const _hoisted_7 = { class: "field-with-label__value" }
const _hoisted_8 = { class: "resident-notification-drawer__description" }
const _hoisted_9 = { class: "resident-notification-drawer__description-text" }
const _hoisted_10 = {
  key: 0,
  class: "resident-notification-drawer__files"
}
const _hoisted_11 = { class: "resident-notification-drawer__file-list" }
const _hoisted_12 = ["src"]
const _hoisted_13 = { class: "resident-notification-drawer__file-content" }
const _hoisted_14 = ["href"]
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { class: "resident-notification-drawer__reply" }
const _hoisted_17 = { class: "resident-notification-drawer__reply-footer" }
const _hoisted_18 = { class: "resident-notification-drawer__reply-counter" }
const _hoisted_19 = { class: "resident-notification-drawer__footer" }
const _hoisted_20 = ["disabled"]

import { computed, h, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import StcNotificationContent from '@/js/components/stc-notification/stc-notification-content.vue';
import ContentTagsHtml from '@/js/components/content-tags/content-tags-html.vue';
import '@/js/components/stc-notification/stc-notification.scss';
import type { NotificationDrawerPayload } from './types';
import {
  fetchNotificationDetail as fetchNotificationDetailApi,
  markNotificationRead,
  replyToNotification,
} from '../resident-notifications/notification-api';
import {
  formatNotificationDate,
  normalizeNotificationFiles,
} from '../resident-notifications/map-notification';
import type { NotificationFile } from '../resident-notifications/types';
import { getDocFormatIconPath } from '@/js/utils/file-upload-utils';

const REPLY_MAX_LENGTH = 512;
const REPLY_SUCCESS_MESSAGE = 'Ваш ответ отправлен';
const REPLY_ERROR_MESSAGE = 'Не удалось отправить ответ';

const NOTIFICATION_ICON_SIZE = 24;


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props, { expose: __expose }) {

const [notificationApi, contextHolder] = notification.useNotification();

const open = ref(false);
const loading = ref(false);
const isSubmitting = ref(false);
const reply = ref('');

const detail = reactive({
  id: undefined as number | undefined,
  title: '',
  date: '',
  description: '',
  files: [] as NotificationFile[],
});

const canSubmitReply = computed(() => reply.value.trim().length > 0);

function showReplySuccessNotification(description = REPLY_SUCCESS_MESSAGE): void {
  notificationApi.open({
    message: h(StcNotificationContent, {
      type: 'success',
      description,
      iconSize: NOTIFICATION_ICON_SIZE,
    }),
    placement: 'topRight',
    duration: 10,
    class: 'stc-notification',
    icon: null,
  });
}

function showReplyErrorNotification(description = REPLY_ERROR_MESSAGE): void {
  notificationApi.open({
    message: h(StcNotificationContent, {
      type: 'error',
      description,
      iconSize: NOTIFICATION_ICON_SIZE,
    }),
    placement: 'topRight',
    duration: 10,
    class: 'stc-notification',
    icon: null,
  });
}

function resetDrawer(): void {
  detail.id = undefined;
  detail.title = '';
  detail.date = '';
  detail.description = '';
  detail.files = [];
  reply.value = '';
  loading.value = false;
  isSubmitting.value = false;
}

function closeDrawer(): void {
  open.value = false;
  resetDrawer();
}

function applyPayload(payload: NotificationDrawerPayload): void {
  detail.id = payload.id;
  detail.title = payload.title;
  detail.date = payload.date;
  detail.description = payload.description;
}

async function loadNotificationDetail(id: number): Promise<void> {
  loading.value = true;

  try {
    const [data] = await Promise.all([fetchNotificationDetailApi(id), markNotificationRead(id)]);

    detail.title = data.subject ?? detail.title;
    detail.date = data.createdAt ? formatNotificationDate(data.createdAt) : detail.date;
    detail.description = data.message ?? detail.description;
    detail.files = normalizeNotificationFiles(data.files);

    window.dispatchEvent(new CustomEvent('residentNotifications:changed'));
  } catch (error) {
    showReplyErrorNotification(
      error instanceof Error ? error.message : 'Не удалось загрузить уведомление',
    );
  } finally {
    loading.value = false;
  }
}

async function showDrawer(payload: NotificationDrawerPayload): Promise<void> {
  resetDrawer();
  applyPayload(payload);
  open.value = true;

  if (payload.id) {
    await loadNotificationDetail(payload.id);
  }
}

async function submitReply(): Promise<void> {
  const message = reply.value.trim();
  if (!message || isSubmitting.value) return;

  if (!detail.id) {
    showReplySuccessNotification();
    closeDrawer();
    return;
  }

  isSubmitting.value = true;

  try {
    await replyToNotification(detail.id, {
      subject: `Re: ${detail.title}`,
      message,
    });

    showReplySuccessNotification();
    window.dispatchEvent(new CustomEvent('residentNotifications:changed'));
    closeDrawer();
  } catch (error) {
    showReplyErrorNotification(error instanceof Error ? error.message : REPLY_ERROR_MESSAGE);
  } finally {
    isSubmitting.value = false;
  }
}

__expose({ open: showDrawer });

return (_ctx: any,_cache: any) => {
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_drawer = _resolveComponent("a-drawer")!
  const _component_a_app = _resolveComponent("a-app")!

  return (_openBlock(), _createBlock(_component_a_app, null, {
    default: _withCtx(() => [
      _createVNode(_unref(contextHolder)),
      _createVNode(_component_a_drawer, {
        width: 720,
        open: open.value,
        class: "resident-notification-drawer",
        "body-style": { paddingBottom: '96px' },
        "header-style": { padding: '32px 40px', border: 'none' },
        "footer-style": { padding: '20px 40px 40px 40px' },
        closable: true,
        onClose: closeDrawer
      }, {
        title: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createElementVNode("span", { class: "resident-notification-drawer__title" }, "Уведомление", -1)
        ]))]),
        closeIcon: _withCtx(() => [
          _createElementVNode("span", _hoisted_1, [
            _createVNode(_unref(CloseOutlined))
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary",
              disabled: loading.value || isSubmitting.value || !canSubmitReply.value,
              onClick: submitReply
            }, _toDisplayString(isSubmitting.value ? 'Отправка...' : 'Отправить ответ'), 9, _hoisted_20)
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_a_divider, { class: "resident-notification-drawer__divider" }),
          (loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Загрузка..."))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("section", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "field-with-label__label" }, "Тема", -1)),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(detail.title), 1)
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    _cache[3] || (_cache[3] = _createElementVNode("span", { class: "field-with-label__label" }, "Дата / время", -1)),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(detail.date), 1)
                  ])
                ]),
                _createElementVNode("section", _hoisted_8, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "field-with-label__label" }, "Описание проекта", -1)),
                  _createElementVNode("div", _hoisted_9, [
                    _createVNode(ContentTagsHtml, {
                      html: detail.description
                    }, null, 8, ["html"])
                  ])
                ]),
                (detail.files.length)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_10, [
                      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "field-with-label__label" }, "Прикреплённые файлы", -1)),
                      _createElementVNode("ul", _hoisted_11, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(detail.files, (file, index) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: `${file.url}-${file.name}-${index}`,
                            class: "resident-notification-drawer__file-item"
                          }, [
                            _createElementVNode("img", {
                              src: _unref(getDocFormatIconPath)(file.name),
                              alt: "",
                              class: "resident-notification-drawer__file-icon"
                            }, null, 8, _hoisted_12),
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("a", {
                                href: file.url,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                download: ""
                              }, _toDisplayString(file.name), 9, _hoisted_14),
                              (file.type || file.size)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString([file.type, file.size].filter(Boolean).join(' / ')), 1))
                                : _createCommentVNode("", true)
                            ])
                          ]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("section", _hoisted_16, [
                  _createVNode(_component_a_form, {
                    layout: "vertical",
                    class: "resident-notification-drawer__reply-form"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_form_item, {
                        class: "stc-ant-field-skin resident-notification-drawer__reply-field",
                        label: "Ответ*"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_a_textarea, {
                            value: reply.value,
                            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((reply).value = $event)),
                            rows: 6,
                            maxlength: REPLY_MAX_LENGTH,
                            placeholder: "Введите ответ"
                          }, null, 8, ["value"]),
                          _createElementVNode("div", _hoisted_17, [
                            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "resident-notification-drawer__reply-note" }, "*не обязателен", -1)),
                            _createElementVNode("span", _hoisted_18, _toDisplayString(reply.value.length) + " / " + _toDisplayString(REPLY_MAX_LENGTH), 1)
                          ])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ], 64))
        ]),
        _: 1
      }, 8, ["open"])
    ]),
    _: 1
  }))
}
}

})